import { createFileRoute } from "@tanstack/react-router";
import { Building, Folder, LogOut, Mail, Phone, User } from "lucide-react";
import { PageHeader } from "@/components/layout";
import { Badge, Button, Card } from "@/components/ui";
import { getSession, signOut } from "@/data/auth";
import { getUserProjects, type ProjectWithStats } from "@/data/projects";

export const Route = createFileRoute("/_authenticated/profile")({
	loader: async () => {
		const [session, projects] = await Promise.all([
			getSession(),
			getUserProjects(),
		]);
		return { session, projects };
	},
	component: ProfilePage,
});

function ProfilePage() {
	const { session, projects } = Route.useLoaderData();

	const handleSignOut = async () => {
		await signOut();
		window.location.href = "/login";
	};

	const user = session?.user;

	return (
		<div>
			<PageHeader
				title="Profile"
				description="Manage your account and view your projects"
			/>

			<div className="grid gap-6 md:grid-cols-3">
				{/* User Info */}
				<Card className="md:col-span-2">
					<h3 className="mb-6 flex items-center gap-2 font-semibold text-lg text-text-primary">
						<User className="h-5 w-5 text-accent" />
						Account Information
					</h3>

					<div className="space-y-6">
						{/* Avatar and Name */}
						<div className="flex items-center gap-4">
							<div className="flex h-16 w-16 items-center justify-center rounded-full bg-accent/20 font-bold text-accent text-xl">
								{user?.name
									? user.name
											.split(" ")
											.map((n) => n[0])
											.join("")
											.toUpperCase()
											.slice(0, 2)
									: user?.email?.[0]?.toUpperCase() || "U"}
							</div>
							<div>
								<h4 className="font-semibold text-lg text-text-primary">
									{user?.name || "User"}
								</h4>
								<Badge variant="accent">Customer</Badge>
							</div>
						</div>

						{/* Contact Info */}
						<div className="grid gap-4 sm:grid-cols-2">
							{user?.email && (
								<div className="flex items-center gap-3 rounded-lg bg-bg-elevated p-3">
									<Mail className="h-5 w-5 text-text-tertiary" />
									<div>
										<p className="text-text-tertiary text-xs uppercase tracking-wider">
											Email
										</p>
										<p className="text-sm text-text-primary">{user.email}</p>
									</div>
								</div>
							)}
							{(user as { phoneNumber?: string })?.phoneNumber && (
								<div className="flex items-center gap-3 rounded-lg bg-bg-elevated p-3">
									<Phone className="h-5 w-5 text-text-tertiary" />
									<div>
										<p className="text-text-tertiary text-xs uppercase tracking-wider">
											Phone
										</p>
										<p className="text-sm text-text-primary">
											{(user as { phoneNumber?: string }).phoneNumber}
										</p>
									</div>
								</div>
							)}
						</div>
					</div>

					{/* Sign Out */}
					<div className="mt-8 border-border border-t pt-6">
						<Button
							variant="danger"
							icon={<LogOut className="h-4 w-4" />}
							onClick={handleSignOut}
						>
							Sign Out
						</Button>
					</div>
				</Card>

				{/* Projects */}
				<Card>
					<h3 className="mb-4 flex items-center gap-2 font-semibold text-lg text-text-primary">
						<Folder className="h-5 w-5 text-accent" />
						Your Projects
					</h3>

					{projects.length > 0 ? (
						<div className="space-y-3">
							{projects.map((project) => (
								<ProjectCard key={project.id} project={project} />
							))}
						</div>
					) : (
						<div className="py-8 text-center">
							<Building className="mx-auto mb-3 h-10 w-10 text-text-tertiary" />
							<p className="text-sm text-text-tertiary">
								No projects assigned yet
							</p>
						</div>
					)}
				</Card>
			</div>
		</div>
	);
}

// Check if string is likely an emoji (short and contains emoji characters)
function isEmoji(str: string): boolean {
	// Emoji regex pattern - matches most common emojis
	const emojiRegex = /[\p{Emoji}]/u;
	return str.length <= 4 && emojiRegex.test(str);
}

function ProjectCard({ project }: { project: ProjectWithStats }) {
	const progress =
		project.totalIssues > 0
			? Math.round(
					((project.totalIssues - project.openIssues) / project.totalIssues) *
						100,
				)
			: project.progress;

	return (
		<div className="rounded-lg bg-bg-elevated p-3 transition-colors hover:bg-bg-elevated/80">
			<div className="mb-2 flex items-center gap-2">
				{project.icon && isEmoji(project.icon) ? (
					<span className="text-lg">{project.icon}</span>
				) : (
					<Folder className="h-4 w-4 text-text-tertiary" />
				)}
				<h4 className="truncate font-medium text-sm text-text-primary">
					{project.name}
				</h4>
			</div>
			<div className="flex items-center gap-2">
				<div className="h-1.5 flex-1 overflow-hidden rounded-full bg-bg-secondary">
					<div
						className="h-full rounded-full bg-accent"
						style={{ width: `${progress}%` }}
					/>
				</div>
				<span className="font-mono text-text-tertiary text-xs">
					{progress}%
				</span>
			</div>
			<div className="mt-2 flex justify-between text-text-tertiary text-xs">
				<span>{project.totalIssues} issues</span>
				<span>{project.openIssues} open</span>
			</div>
		</div>
	);
}
